/**
 * PeriodSelector - Period quick buttons + month/year range dropdowns.
 */

import React, { useMemo } from 'react';

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

interface PeriodSelectorProps {
    period: string;
    onPeriodChange: (period: string) => void;
    /** "YYYY-MM" format */
    startMonth: string;
    endMonth: string;
    onStartMonthChange: (month: string) => void;
    onEndMonthChange: (month: string) => void;
    /** Bounds from API: "YYYY-MM" */
    minMonth: string | null;
    maxMonth: string | null;
}

const PERIODS = [
    { value: 'all', label: 'All' },
    { value: '7d', label: '1W' },
    { value: '30d', label: '1M' },
    { value: '90d', label: '3M' },
    { value: '1y', label: '1Y' },
];

/** Build year list from minMonth to maxMonth */
const buildYears = (minMonth: string | null, maxMonth: string | null): number[] => {
    const now = new Date();
    const minY = minMonth ? parseInt(minMonth.split('-')[0]) : now.getFullYear() - 2;
    const maxY = maxMonth ? parseInt(maxMonth.split('-')[0]) : now.getFullYear();
    const years: number[] = [];
    for (let y = minY; y <= maxY; y++) years.push(y);
    return years;
};

const MonthYearPicker: React.FC<{
    value: string;
    onChange: (v: string) => void;
    years: number[];
    label: string;
}> = ({ value, onChange, years, label }) => {
    const [year, month] = useMemo(() => {
        const parts = value.split('-');
        return [parseInt(parts[0]) || years[0] || 2026, parseInt(parts[1]) || 1];
    }, [value, years]);

    const handleMonthChange = (m: number) => {
        onChange(`${year}-${String(m).padStart(2, '0')}`);
    };
    const handleYearChange = (y: number) => {
        onChange(`${y}-${String(month).padStart(2, '0')}`);
    };

    return (
        <div className="dashboard__month-picker">
            <span className="dashboard__month-picker-label">{label}</span>
            <select
                className="dashboard__month-select"
                value={month}
                onChange={(e) => handleMonthChange(Number(e.target.value))}
            >
                {MONTHS.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                ))}
            </select>
            <select
                className="dashboard__year-select"
                value={year}
                onChange={(e) => handleYearChange(Number(e.target.value))}
            >
                {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                ))}
            </select>
        </div>
    );
};

const PeriodSelector: React.FC<PeriodSelectorProps> = ({
    period, onPeriodChange,
    startMonth, endMonth, onStartMonthChange, onEndMonthChange,
    minMonth, maxMonth,
}) => {
    const years = useMemo(() => buildYears(minMonth, maxMonth), [minMonth, maxMonth]);

    const handlePeriodClick = (value: string) => {
        onPeriodChange(value);
    };

    return (
        <div className="dashboard__period-selector">
            <div className="dashboard__period-group">
                {PERIODS.map((p) => (
                    <button
                        key={p.value}
                        className={`dashboard__period-btn ${period === p.value ? 'dashboard__period-btn--active' : ''}`}
                        onClick={() => handlePeriodClick(p.value)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            <div className="dashboard__date-range">
                <MonthYearPicker
                    label="From"
                    value={startMonth}
                    onChange={onStartMonthChange}
                    years={years}
                />
                <span className="dashboard__date-separator">—</span>
                <MonthYearPicker
                    label="To"
                    value={endMonth}
                    onChange={onEndMonthChange}
                    years={years}
                />
            </div>
        </div>
    );
};

export default PeriodSelector;
